/**
 * The "Quotes Pending" tag (item 7a), and the one fact it is derived from.
 *
 * ## The source of truth is `is_shared`, and only `is_shared`
 *
 * A quote is PENDING while the broker has prepared it but not yet put it in
 * front of the customer. That is exactly `QuoteRequest.is_shared === false`
 * (`backend/apps/quotes/models.py`, indexed, a `filterset_field`, and already
 * on the list serializer so a list can badge without a round trip per row).
 * The tag clears the moment `share/` or `share-via/` runs, because those are
 * the only two things that set the flag.
 *
 * It is deliberately NOT derived from:
 *
 *  * **`status`.** `QuoteRequestViewSet._publish` moves the status inside
 *    `try/except InvalidTransition: pass`, so a share can succeed with the
 *    status left exactly where it was. The code comment there says it plainly:
 *    "sharing sets the status, the status never sets `is_shared`". A tag keyed
 *    on the status would go on lying after a successful share.
 *  * **The activity log.** The `shared` entry is only written on the
 *    backfill-repair branch, not on a normal first publish.
 *  * **`shared_at`.** `unshare` deliberately keeps it, so it is an "ever
 *    shared" high-water mark, not "shared right now".
 *
 * ## Why this module lives under `pages/quotes/`
 *
 * Same reason as `quoteEnquiries.ts` next door: the directories a hook or a
 * shared component would normally live in are outside this change's file
 * domain. It uses the shared axios instance, so auth, refresh and throttling
 * behave identically to every other call.
 *
 * ## Where it is rendered
 *
 * Two call sites, and they must stay call sites — this module shipped once with
 * the data half done, the component written and NOTHING rendering it, which is
 * the same as not having built it:
 *
 *  * `pages/customers/CustomerDetailPage.tsx` — under the contact's name,
 *    beside "Create New Quote", which is the point: you see the outstanding
 *    quote and act on it from the same header.
 *  * `pages/leads/LeadDetailPage.tsx` — for a CONVERTED lead, keyed on the
 *    contact the conversion created (`converted_to_customer`). An unconverted
 *    lead has no contact and so nothing to count.
 *
 * Pass the **contacts.Customer id**, the same id `/contacts/:id` is keyed by,
 * NOT the login's User id. The server's `?customer=` handling folds in
 * duplicate contact rows, so the tag agrees with the Contact Details Quotes tab
 * about who the person is. `__tests__` on both pages pin the wiring.
 */

import { useQuery } from '@tanstack/react-query';

import { Badge } from '@/components/common';
import api from '@/services/api';
import type { PaginatedResponse, Quote } from '@/types';

/**
 * A quote-request list row, plus `is_shared`.
 *
 * `is_shared` IS on the wire (`QuoteRequestListSerializer.Meta.fields`) but is
 * absent from the shared `Quote` interface, which lives outside this change's
 * file domain. Declared here rather than cast away at each use site, so the
 * flag stays typed.
 */
export type QuoteRow = Quote & { is_shared?: boolean };

/** A quote is "pending" until the broker shares it with the customer. */
// eslint-disable-next-line react-refresh/only-export-components
export function isQuotePending(quote: Pick<QuoteRow, 'is_shared'>): boolean {
  return quote.is_shared === false;
}

/**
 * How many of this contact's quotes are still unshared.
 *
 * `page_size=1` because only `count` is read — the rows themselves are never
 * rendered from here, and pulling a contact's whole quote book to badge a
 * heading would be wasteful.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useQuotesPendingCount(customerId: string | null | undefined) {
  return useQuery({
    queryKey: ['quotes-pending', customerId],
    enabled: !!customerId,
    queryFn: async () => {
      const params = new URLSearchParams({
        customer: String(customerId),
        is_shared: 'false',
        page_size: '1',
      });
      const response = await api.get<PaginatedResponse<QuoteRow>>(
        `/quotes/requests/?${params.toString()}`
      );
      return response.data.count;
    },
  });
}

/**
 * The tag itself, for a contact/converted-lead heading.
 *
 * Three outcomes, never two:
 *  * pending quotes exist -> the tag,
 *  * none -> nothing at all (the absence IS the answer),
 *  * the request FAILED -> a muted "Quotes status unavailable", never silence.
 *
 * That last branch is the point. Rendering nothing on a failed fetch would say
 * "this contact has no pending quotes" on the strength of a request that never
 * answered — the exact shape of `feedback_failed_request_is_not_empty`.
 */
export function ContactQuotesPendingTag({
  customerId,
  className,
}: {
  customerId: string | null | undefined;
  className?: string;
}) {
  const { data: count, isLoading, isError } = useQuotesPendingCount(customerId);

  if (!customerId || isLoading) return null;

  if (isError) {
    return (
      <span title="Could not check for pending quotes">
        <Badge variant="default" size="sm" className={className}>
          Quotes status unavailable
        </Badge>
      </span>
    );
  }

  if (!count) return null;

  return (
    <Badge variant="warning" size="sm" className={className}>
      {count === 1 ? 'Quote Pending' : `${count} Quotes Pending`}
    </Badge>
  );
}

/**
 * The same fact stated on a single quote row, from the CUSTOMER's side.
 *
 * The customer never sees the word "pending" about their broker's internal
 * work — from their side the useful distinction is whether the quote has been
 * sent to them yet.
 */
export function SharedStatusBadge({ isShared }: { isShared?: boolean }) {
  if (isShared === undefined) return null;
  return isShared ? (
    <Badge variant="success" size="sm">
      Shared with you
    </Badge>
  ) : (
    <Badge variant="warning" size="sm">
      Being prepared
    </Badge>
  );
}
